// Entry point da aplicação: define os componentes raiz (App/InnerApp) que nunca são
// alvo de fast-refresh. A regra react-refresh não se aplica aqui — desabilitada no arquivo.
/* eslint-disable react-refresh/only-export-components */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { AuthProvider, useAuth } from '@/features/auth/components/auth-provider'
import '@/styles/globals.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always refetch on mount (no stale window)
      gcTime: 1000 * 60 * 30, // 30 minutes (keep cache for instant display while refetching)
      refetchOnMount: 'always', // Force refetch every time component mounts (page navigation)
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      refetchOnWindowFocus: false,
    },
  },
})

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()
  return <RouterProvider router={router} context={{ auth, queryClient }} />
}

function App() {
  // OTIMIZAÇÃO: Handler simplificado - dispara invalidate sem bloquear
  const handleAuthStateChange = (event: string) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
      // Dispara invalidate sem await - não bloqueia o fluxo
      router.invalidate().catch((error) => {
        console.error('[App] Router invalidation error:', error)
      })
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider onAuthStateChange={handleAuthStateChange}>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
)
